import { useCallback, useRef, useState } from "react";
import { getMergedCachedData, setCachedDocument } from "../utils/documentCache";

/**
 * useDocumentCache
 *
 * Thin orchestration layer over documentCache.js. Keeps the cache lifecycle
 * (instant-render read, UI lock state, fresh-data commit) out of the core
 * SpreadsheetContainer logic.
 *
 * @param {string} documentId
 * @param {boolean} enabled  Caching only applies to normal documents (not
 *                           templates / read-only / conflict-preview / offline).
 */
export function useDocumentCache(documentId, enabled) {
  // Read the cached payload once on mount so the instant render is deterministic.
  const [cached] = useState(() =>
    enabled && documentId ? getMergedCachedData(documentId) : null
  );

  const [isUiLocked, setIsUiLocked] = useState(false);

  // Guards a single instant cached paint so it cannot loop / re-trigger.
  const cacheRenderedRef = useRef(false);

  const lockUi = useCallback(() => setIsUiLocked(true), []);
  const unlockUi = useCallback(() => setIsUiLocked(false), []);

  /**
   * Persist the latest merged, user-visible state.
   * `documentData` must be derived from in-memory sheets (not stale API data).
   */
  const commitCache = useCallback(
    (documentData, documentInfo) => {
      if (!enabled || !documentId) return;
      setCachedDocument(documentId, { documentData, documentInfo });
    },
    [enabled, documentId]
  );

  return {
    cached,
    isUiLocked,
    lockUi,
    unlockUi,
    commitCache,
    cacheRenderedRef,
  };
}

export default useDocumentCache;
